import React, { useEffect, useState } from "react";
import InstructorLayout from "@/components/InstructorLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ClipboardCheck, FolderOpen, MessageSquare, CalendarCheck } from "lucide-react";
import UpcomingSessionsWidget from "@/components/UpcomingSessionsWidget";

export default function InstructorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ tareas_pendientes: 0, entregas_por_calificar: 0, mensajes_no_leidos: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([
          api.get("/instructor/assignments"),
          api.get("/messages/threads"),
        ]);
        const items = a.data.items || [];
        const activeTareas = items.filter(x => x.status !== "archived").length;
        const porCalificar = items.reduce((sum, x) => sum + Math.max(0, (x.deliveries?.total || 0) - (x.deliveries?.graded || 0)), 0);
        const unread = (t.data.items || []).reduce((sum, x) => sum + (x.unread_count || 0), 0);
        setStats({ tareas_pendientes: activeTareas, entregas_por_calificar: porCalificar, mensajes_no_leidos: unread });
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <InstructorLayout>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Instructor</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Hola, {user?.full_name}</h1>
        <p className="mt-2 text-gray-400 text-sm">Gestiona cohortes, sesiones vivo, tareas, biblioteca y mensajes con tus alumnos.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-8 max-w-3xl">
        <StatCard label="Tareas publicadas" value={stats.tareas_pendientes} icon={ClipboardCheck} to="/app/instructor/tareas" />
        <StatCard label="Entregas por calificar" value={stats.entregas_por_calificar} icon={ClipboardCheck} to="/app/instructor/tareas" highlight={stats.entregas_por_calificar > 0} />
        <StatCard label="Mensajes sin leer" value={stats.mensajes_no_leidos} icon={MessageSquare} to="/app/instructor/mensajes" highlight={stats.mensajes_no_leidos > 0} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
        <UpcomingSessionsWidget ownerType="instructor" limit={5} />
        <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="instructor-quick-links">
          <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
            <CalendarCheck className="w-4 h-4 text-[#F26A21]" /> Acciones rápidas
          </h3>
          <a href="/app/instructor/calendario" className="block py-2 text-sm text-gray-300 hover:text-white">Ver calendario y crear sesión →</a>
          <a href="/app/instructor/tareas" className="block py-2 text-sm text-gray-300 hover:text-white">Publicar una nueva tarea →</a>
          <a href="/app/instructor/biblioteca" className="block py-2 text-sm text-gray-300 hover:text-white">Subir material a la biblioteca →</a>
          <a href="/app/instructor/mensajes" className="block py-2 text-sm text-gray-300 hover:text-white">Conversaciones con alumnos →</a>
        </div>
      </div>
    </InstructorLayout>
  );
}

function StatCard({ label, value, icon: Icon, to, highlight }) {
  return (
    <a href={to} data-testid={`instructor-stat-${label.replace(/\s/g, "-").toLowerCase()}`}
       className={`rounded-2xl border p-4 transition-colors ${highlight ? "border-[#F26A21]/40 bg-[#F26A21]/10 hover:bg-[#F26A21]/15" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${highlight ? "text-[#F26A21]" : "text-gray-400"}`} />
        <p className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">{label}</p>
      </div>
      <p className="mt-2 text-3xl font-bold text-white">{value}</p>
    </a>
  );
}
